import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import { cn } from "@repo/ui/lib/utils";
import { type KeyboardEvent, type Ref, useEffect, useId, useRef, useState } from "react";

export interface PillOption {
  value: string;
  label: string;
  hint?: string;
}

interface PillComboboxProps {
  /** The text in the pill. Typing changes it; a pick replaces it with the option's label. */
  text: string;
  onText: (text: string) => void;
  /** Shown when empty, and read by screen readers as the pill's name. */
  label: string;
  options: readonly PillOption[];
  /** Return false to stay on this pill, e.g. a spelling fix that searches again. */
  onPick: (option: PillOption) => boolean | undefined;
  /** Shown in place of options while they load or when there are none. */
  status?: string;
  /** Enter, or a pick: the pill is answered, move on. */
  onAdvance: () => void;
  /** Escape with nothing open: leave the phrase. */
  onExit: () => void;
  inputRef?: Ref<HTMLInputElement>;
  autoFocus?: boolean;
  /** Options are still arriving: Enter waits for them rather than taking what is shown. */
  busy?: boolean;
}

/** The list's height at most (Tailwind max-h-56), its gap from the blank, and its margin from the window's edge, in px. */
const LIST_HEIGHT = 224;
const LIST_GAP = 4;
const EDGE = 8;

export const PILL_CLASS =
  // Exactly one line tall, border included, so filling a phrase never moves the lines around it.
  "box-border h-[1lh] rounded border px-1 py-0 align-baseline text-[length:inherit] leading-[inherit] outline-none [field-sizing:content] focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background";

export function pillStateClass(empty: boolean) {
  return empty
    ? "min-w-8 border-dashed border-border bg-muted/60 text-muted-foreground placeholder:text-muted-foreground"
    : "border-transparent bg-muted text-foreground";
}

/**
 * One blank in a phrase: type to filter, arrows to choose, Enter to take it and
 * move on. Tab moves between blanks as it would between form fields, taking the
 * highlighted option first when the typed text was only a filter.
 *
 * Its own list rather than the shared Combobox: the phrase is a React node view
 * inside the editor, and opening the Combobox's portalled popup there remounts
 * every node view in the note, which closes the phrase being filled. It is
 * placed the way that popup is, by Floating UI, without the portal.
 */
export function PillCombobox({
  text,
  onText,
  label,
  options,
  onPick,
  status,
  onAdvance,
  onExit,
  inputRef,
  autoFocus,
  busy,
}: PillComboboxProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // A new list starts at its top, which is what makes type-then-Enter work.
  const listKey = options.map((option) => option.value).join("\u0000");
  const [shownList, setShownList] = useState(listKey);
  if (shownList !== listKey) {
    setShownList(listKey);
    setActive(0);
  }
  // Enter takes the highlighted row, as it reads. Tab only takes it once
  // something was typed or arrowed to, so it can still pass an empty blank by.
  const [arrowed, setArrowed] = useState(false);
  const pointed = useRef(false);
  const showList = open && (options.length > 0 || Boolean(status));
  const activeOption = showList ? options[active] : undefined;
  const chosen = activeOption && (text.trim() || arrowed) ? activeOption : undefined;

  // Enter typed ahead of the search: taken once the results are in, so a fast
  // typist gets the coded drug, not the words as typed.
  const enterPending = useRef(false);
  useEffect(() => {
    if (busy || !enterPending.current) return;
    enterPending.current = false;
    if (!activeOption || pick(activeOption)) onAdvance();
  });

  // Below the blank, flipped above when there is no room, kept on screen and
  // no taller than the room, following the blank as the page scrolls. Fixed, so
  // the note's own overflow can't clip it.
  const { refs, floatingStyles } = useFloating({
    open: showList,
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(LIST_GAP),
      flip({ padding: EDGE }),
      shift({ padding: EDGE }),
      size({
        padding: EDGE,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(0, Math.min(LIST_HEIGHT, availableHeight))}px`;
        },
      }),
    ],
  });

  // Arrowing past the visible rows scrolls the list with the active one.
  useEffect(() => {
    if (showList) document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [showList, listId, active]);

  /** Whether to move on. */
  const pick = (option: PillOption) => {
    const advance = onPick(option) !== false;
    if (advance) setOpen(false);
    return advance;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (!options.length) return;
      setArrowed(true);
      const step = event.key === "ArrowUp" ? -1 : 1;
      setActive((index) => (index + step + options.length) % options.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (busy) {
        enterPending.current = true;
        return;
      }
      if (!activeOption || pick(activeOption)) onAdvance();
      return;
    }
    if (event.key === "Tab" && !event.shiftKey && chosen && chosen.label !== text) {
      pick(chosen);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (showList) setOpen(false);
      else onExit();
    }
  };

  return (
    <span ref={refs.setReference} className="inline-block">
      <input
        ref={inputRef}
        // biome-ignore lint/a11y/noAutofocus: a phrase opens with its first blank focused, the way it was typed.
        autoFocus={autoFocus}
        role="combobox"
        aria-label={label}
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={activeOption ? `${listId}-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={label}
        value={text}
        onChange={(event) => {
          onText(event.target.value);
          enterPending.current = false;
          setArrowed(false);
          setOpen(true);
        }}
        onPointerDown={() => {
          pointed.current = true;
        }}
        onFocus={(event) => {
          // Arriving by keyboard or by the phrase opening lands at the end of the
          // answer, ready to keep typing; a click keeps the caret where it landed.
          if (!pointed.current) {
            const end = event.currentTarget.value.length;
            event.currentTarget.setSelectionRange(end, end);
          }
          pointed.current = false;
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className={cn(PILL_CLASS, pillStateClass(!text))}
      />
      {showList && (
        <div
          id={listId}
          role="listbox"
          aria-label={label}
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-50 w-max min-w-56 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground text-sm shadow-md"
        >
          {options.length === 0 && <div className="px-2 py-1.5 text-muted-foreground">{status}</div>}
          {options.map((option, index) => (
            // biome-ignore lint/a11y/useFocusableInteractive: focus stays in the input; the active row follows aria-activedescendant.
            <div
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={cn(
                "flex cursor-pointer items-baseline justify-between gap-3 rounded-sm px-2 py-1.5",
                index === active && "bg-accent text-accent-foreground",
              )}
              // mousedown, not click: the input must not blur and close the list first.
              onMouseDown={(event) => {
                event.preventDefault();
                if (pick(option)) onAdvance();
              }}
            >
              <span className="whitespace-nowrap">{option.label}</span>
              {option.hint && (
                <span className="shrink-0 whitespace-nowrap text-muted-foreground text-xs">{option.hint}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
